import * as React from 'react';

import type { AudienceFilterSpec } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { getUserFilterPreset } from 'bundles/enterprise-admin-user-filters/constants';
import { useCreateAdminUserFilterMutation } from 'bundles/enterprise-admin-user-filters/hooks';
import type { ActivityFilter, ActivityFilterMapped, UserFilter } from 'bundles/enterprise-admin-user-filters/types';
import {
  buildAssignedContentFilter,
  mapContentSelectionValueToGRPC,
  mapUserFilterFromClientToServerModel,
} from 'bundles/enterprise-admin-user-filters/utils';

/**
 * Translate the semantic audience spec into the client-side `UserFilter` the
 * filter mappers understand. Program-scoped funnel reminders always apply the
 * triggering program as a hard scope (`programIdSelection`); org-wide home
 * audiences reuse the canonical `getUserFilterPreset` filters — the exact same
 * single-membership filters the legacy preset messages target.
 */
export const buildClientUserFilter = (spec: AudienceFilterSpec): UserFilter => {
  switch (spec.audienceType) {
    case 'INVITED_NOT_JOINED':
      return {
        primaryFilter: {
          type: 'INVITED',
          programIdSelection: { values: [spec.programId] },
        },
      };
    case 'JOINED_NOT_ENROLLED':
      return {
        primaryFilter: {
          type: 'MEMBER',
          programIdSelection: { values: [spec.programId] },
          activityFilter: { type: 'NO_ENROLLMENTS' },
        },
      };
    case 'ORG_INVITED_NOT_JOINED':
      return getUserFilterPreset('invited').filter;
    case 'ORG_ALL_JOINED':
      return getUserFilterPreset('joined').filter;
    case 'ORG_JOINED_NOT_ENROLLED':
      return getUserFilterPreset('joined-not-enrolled').filter;
    case 'ORG_INACTIVE_LEARNERS':
      return getUserFilterPreset('inactive-30').filter;
    case 'ORG_LONG_TERM_INACTIVE_LEARNERS':
      return getUserFilterPreset('inactive-90').filter;
    case 'ORG_ENROLLED_LEARNERS':
      return getUserFilterPreset('enrolled-in-30-days').filter;
    case 'ORG_RECENT_COMPLETERS':
      return getUserFilterPreset('1-completion-in-30-days').filter;
    case 'ORG_INCOMPLETE_IN_COURSE':
      return buildAssignedContentFilter({
        contentId: spec.courseId,
        contentType: 'COURSE',
        contentName: spec.courseName,
        isEnrolled: true,
        isCompleted: false,
      }).filter;
    case 'JOINED_IN_PROGRAMS':
      // Joined (MEMBER) learners, hard-scoped to the target group(s). An empty list leaves the
      // scope off, which the backend reads as org-wide members.
      return {
        primaryFilter: {
          type: 'MEMBER',
          programIdSelection: { values: spec.programIds },
        },
      };
    case 'ORG_INCOMPLETE_IN_LEARNING_PATH':
      // MULTI_TYPE_FILTER (JOINED + INVITED) rather than `buildAssignedContentFilter`'s MEMBER-only
      // shape — content can be assigned before someone joins, so the reminder should still reach an
      // invited-but-not-yet-joined learner. `useCreateSavedFilter` flattens `assignedContent` onto
      // `activityFilter` and maps `contentType` for this primary filter type — see there for why.
      return {
        primaryFilter: {
          type: 'MULTI_TYPE_FILTER',
          multiFilter: {
            membershipStates: ['JOINED', 'INVITED'],
            programIdSelection: { values: spec.programIds },
            activityFilter: {
              type: 'ASSIGNED_CONTENT',
              assignedContent: {
                contentSelection: { values: [{ contentId: spec.contentId, contentType: 'LEARNING_PATH' }] },
                isCompleted: false,
              },
            },
          },
        },
      };
    default: {
      const exhaustive: never = spec;
      throw new Error(`Unhandled audience spec: ${JSON.stringify(exhaustive)}`);
    }
  }
};

/**
 * Program scope to send with the draft, derived from the audience spec: a single `programId`
 * (funnel reminders) or a `programIds` list (a catalog item added to several groups). Org-wide
 * specs carry neither and resolve to `undefined` (no program scope).
 */
export const getSpecProgramIds = (spec: AudienceFilterSpec): string[] | undefined => {
  if ('programIds' in spec) return spec.programIds;
  if ('programId' in spec) return [spec.programId];
  return undefined;
};

/**
 * Legacy `preset` value for the existing send API's draft payload, derived from
 * the entry point's `audienceType` — deliberately NOT from the generate API's
 * `preset` (a different vocabulary that can misclassify, e.g. it tags this join
 * reminder as `enrollment_reminder`). The existing API still expects the legacy
 * `PresetId` enum from `enterprise-admin-messages`; we keep it as a local string
 * here to avoid taking a cross-bundle dependency just for this interim mapping.
 *
 * TODO: once the BE accepts the new HTML-template payload, pass the new preset
 * returned by the generate API instead of this legacy mapping.
 */
export const getLegacyMessagePreset = (audienceType: AudienceFilterSpec['audienceType']): string => {
  switch (audienceType) {
    case 'INVITED_NOT_JOINED':
    case 'ORG_INVITED_NOT_JOINED':
      return 'remind-join';
    case 'JOINED_NOT_ENROLLED':
    case 'ORG_JOINED_NOT_ENROLLED':
      return 'remind-enroll';
    case 'ORG_ALL_JOINED':
    case 'ORG_INACTIVE_LEARNERS':
      return 'remind-be-active-general-past-30-days';
    case 'ORG_LONG_TERM_INACTIVE_LEARNERS':
      return 'remind-be-active-past-90-days';
    case 'ORG_ENROLLED_LEARNERS':
    case 'JOINED_IN_PROGRAMS':
      return 'remind-complete-past-30-days';
    case 'ORG_RECENT_COMPLETERS':
      return 'congratulate-achievement';
    case 'ORG_INCOMPLETE_IN_COURSE':
    case 'ORG_INCOMPLETE_IN_LEARNING_PATH':
      return 'remind-assigned-learners';
    default:
      throw new Error(`Unhandled audienceType: ${audienceType}`);
  }
};

/**
 * CTA button to attach to the sent message, by audience type.
 * Mirrors the CTA the legacy flow used for each preset so V2 reminders carry
 * the same button. Returned as the literal strings the draft API expects.
 */
export const getMessageCallToAction = (
  audienceType: AudienceFilterSpec['audienceType']
): 'JOIN_PROGRAM' | 'FIND_A_COURSE' | 'CONTINUE_LEARNING' => {
  switch (audienceType) {
    case 'INVITED_NOT_JOINED':
    case 'ORG_INVITED_NOT_JOINED':
      return 'JOIN_PROGRAM';
    case 'JOINED_NOT_ENROLLED':
    case 'ORG_JOINED_NOT_ENROLLED':
    case 'ORG_INACTIVE_LEARNERS':
    case 'ORG_LONG_TERM_INACTIVE_LEARNERS':
    case 'ORG_ALL_JOINED':
      return 'FIND_A_COURSE';
    case 'ORG_ENROLLED_LEARNERS':
    case 'ORG_RECENT_COMPLETERS':
    case 'ORG_INCOMPLETE_IN_COURSE':
    case 'JOINED_IN_PROGRAMS':
    case 'ORG_INCOMPLETE_IN_LEARNING_PATH':
      return 'CONTINUE_LEARNING';
    default: {
      const exhaustive: never = audienceType;
      throw new Error(`Unhandled audienceType: ${exhaustive}`);
    }
  }
};

/**
 * Creates a saved admin user filter (purpose `FILTER_PURPOSE_MESSAGE`) from a
 * client `UserFilter` and returns its `filterId` — the id the draft/publish step
 * sends, so the email reaches exactly the learners the filter resolves to.
 * `userIdentifiers` (mixed ids/emails) narrows the audience to specific learners
 * (a manual learners-table selection); omit it to target the whole filter.
 */
export const useCreateSavedFilter = () => {
  const createAdminUserFilter = useCreateAdminUserFilterMutation();

  return React.useCallback(
    async (orgId: string, clientFilter: UserFilter, userIdentifiers?: string[]): Promise<string | undefined> => {
      const filter = mapUserFilterFromClientToServerModel(
        clientFilter,
        userIdentifiers?.length ? { userIdentifiers } : undefined
      );
      // The filter-schema service validates `contentType` against its own PascalCase enum
      // (Course/Specialization/LearningPath), not our client-side SCREAMING_SNAKE_CASE one — mirrors
      // the same conversion the legacy compose flow (CreateMessage/RecipientDrawer) applies.
      if (filter.primaryFilter.type === 'MEMBER' && filter.primaryFilter?.activityFilter?.type === 'ASSIGNED_CONTENT') {
        filter.primaryFilter.activityFilter.contentSelection = {
          values: filter.primaryFilter.activityFilter.contentSelection.values.map(mapContentSelectionValueToGRPC),
        };
      } else if (filter.primaryFilter.type === 'MULTI_TYPE_FILTER') {
        // `mapUserFilterFromClientToServerModel` puts MULTI_TYPE_FILTER's `activityFilter` directly on
        // `primaryFilter` (matching the real CreateAdminUserFilter schema, even though
        // `PrimaryFilterMapped` declares it nested under `multiFilter` — the util's own
        // `as unknown as PrimaryFilterMapped` cast already papers over that mismatch). Unlike the
        // MEMBER branch above, it passes the value straight through from the client `UserFilter` shape
        // unflattened and unmapped (nested `assignedContent`, SCREAMING_SNAKE_CASE `contentType`), so
        // both steps happen here instead.
        const primaryFilter = filter.primaryFilter as unknown as {
          activityFilter?: ActivityFilter | ActivityFilterMapped;
        };
        const rawActivityFilter = primaryFilter.activityFilter as ActivityFilter | undefined;
        const assignedContent =
          rawActivityFilter?.type === 'ASSIGNED_CONTENT' ? rawActivityFilter.assignedContent : undefined;
        if (assignedContent) {
          primaryFilter.activityFilter = {
            type: 'ASSIGNED_CONTENT',
            contentSelection: { values: assignedContent.contentSelection.values.map(mapContentSelectionValueToGRPC) },
            isCompleted: assignedContent.isCompleted,
          };
        }
      }
      const response = await createAdminUserFilter({
        variables: {
          input: {
            organizationId: orgId,
            filterName: '',
            filterPurpose: 'FILTER_PURPOSE_MESSAGE',
            filter,
          },
        },
      });
      return response.data?.CreateAdminUserFilterMutation.adminUserFilter.filterId;
    },
    [createAdminUserFilter]
  );
};

/**
 * Spec-based saved filter for the funnel entry points: builds the client filter
 * from the semantic `audienceType`, then creates the saved filter via
 * {@link useCreateSavedFilter}.
 */
export const useCreateMessageFilter = () => {
  const createSavedFilter = useCreateSavedFilter();

  return React.useCallback(
    (spec: AudienceFilterSpec): Promise<string | undefined> =>
      createSavedFilter(spec.orgId, buildClientUserFilter(spec)),
    [createSavedFilter]
  );
};

export default useCreateMessageFilter;
